import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _set from "lodash/set";
import _isArray from "lodash/isArray";
import _mergeWith from "lodash/mergeWith";
import _get from "lodash/get";
/**
 * @module
 *
 * @description Utils for working with meta data.
 */
var _templateObject;
import "core-js/modules/es6.string.link.js";
import { gql } from 'graphql-tag';
import { getCanonicalUrl, getDescription, getFeaturedImageInfoWithFallback, getId, getNofollow, getNoindex, getPageTaxonomyData, getPageTaxonomyDataElem, getSynopsis, getTitleSeo, getType } from '../selectors/common';
var DEFAULT_OG_IMAGE = {
  source_url: 'https://www.nerdwallet.com/assets/img/nw-logos/NW-default_og-image.jpg',
  height: 78,
  width: 150
};

/**
 * Set nerdlayout dynamic options on the server response.
 *
 * @param {Object} options.res Server response.
 * @param {Object} options.wpEntity WordPress canonical entity or query0 post object.
 * @param {Object} options.optionsOverrides Options overrides.
 *
 * @example
 * ```js
 * // Within data-dependencies, for example.
 *
 * const optionsOverrides = {
 *   canonicalURL: 'https://custom-canonical-url.com',
 * };
 *
 * return setNerdLayoutDynamicOptions({
 *   optionsOverrides,
 *   res,
 *   wpEntity: article
 * })
 * ````
 *
 * @return {Object} res Modified server response.
 */
export var setNerdlayoutDynamicOptions = function setNerdlayoutDynamicOptions(_ref) {
  var res = _ref.res,
    wpEntity = _ref.wpEntity,
    _ref$optionsOverrides = _ref.optionsOverrides,
    optionsOverrides = _ref$optionsOverrides === void 0 ? {} : _ref$optionsOverrides;
  try {
    var origOpts = _get(res, 'locals.nerdlayout.dynamicOpts', []);
    var origMeta = _get(res, 'locals.nerdlayout.dynamicMeta', []);
    var title = getTitleSeo(wpEntity);
    var description = getDescription(wpEntity) || getSynopsis(wpEntity);
    var ogImage = getFeaturedImageInfoWithFallback(wpEntity, ['primary-xl', 'full']) || DEFAULT_OG_IMAGE;
    var ogImageUrl = ogImage.source_url;
    var robots = [];
    if (getNofollow(wpEntity)) {
      robots.push('nofollow');
    }
    if (getNoindex(wpEntity)) {
      robots.push('noindex');
    }
    var dynamicMeta = [].concat(_toConsumableArray(origMeta), [{
      // Name is required, otherwise this breaks in global-markup
      name: 'article:publisher',
      property: 'article:publisher',
      content: 'http://www.facebook.com/NerdWallet'
    }, {
      name: 'twitter:card',
      content: 'summary_large_image'
    }, {
      name: 'twitter:description',
      content: description
    }, {
      name: 'twitter:site',
      content: '@NerdWallet'
    }, {
      name: 'twitter:image',
      content: ogImageUrl
    }]);
    var canonicalURL = getCanonicalUrl(wpEntity);
    var dynamicOpts = {
      canonicalURL: canonicalURL,
      description: description,
      dynamicMeta: dynamicMeta,
      imageSrc: ogImageUrl,
      ogTags: {
        title: title,
        description: description,
        image: ogImageUrl,
        'image:secure_url': ogImageUrl,
        'image:width': ogImage.width,
        'image:height': ogImage.height,
        locale: 'en_US',
        type: 'article',
        site_name: 'NerdWallet',
        url: canonicalURL
      },
      pageTaxonomy: [getPageTaxonomyDataElem(wpEntity, 'vertical') || 'undefined', getPageTaxonomyDataElem(wpEntity, 'topic') || 'undefined', getPageTaxonomyDataElem(wpEntity, 'pageType') || 'Navigate', getPageTaxonomyDataElem(wpEntity, 'contentType') || 'article'],
      robots: robots,
      title: title,
      wpData: {
        wpPageType: getType(wpEntity),
        wpPageID: "post_".concat(getId(wpEntity))
      }
    };

    // Merge in options overrides to support customization.
    // For `robots`, concatenate the arrays instead of overriding
    var dynamicOptsWithOverrides = _mergeWith(origOpts, dynamicOpts, optionsOverrides, function (objValue, srcValue, key) {
      if (key === 'robots' && _isArray(objValue)) {
        return objValue.concat(srcValue);
      }
      return undefined; // Use default _.merge behavior
    });
    _set(res, 'locals.nerdlayout.dynamicOpts', dynamicOptsWithOverrides);
  } catch (error) {
    throw new Error(error);
  }

  // Return res for testing purposes.
  return res;
};

/**
 * Get nerdlayout dynamic options.
 * @private
 * @param {Object} options.post query0 post object.
 * @param {Object} options.optionsOverrides Options overrides.
 *
 * @example
 * ```js
 * const optionsOverrides = {
 *   canonicalURL: 'https://custom-canonical-url.com',
 * };
 *
 * const nlOpts = getNerdLayoutDynamicOptions({
 *   optionsOverrides,
 *   post: graphqlResponse
 * })
 * ````
 *
 * @return {Object} Dynamic options
 */
export var getNerdlayoutDynamicOptions = function getNerdlayoutDynamicOptions(_ref2) {
  var _post$seo, _post$seo$twitter;
  var post = _ref2.post,
    _ref2$optionsOverride = _ref2.optionsOverrides,
    optionsOverrides = _ref2$optionsOverride === void 0 ? {} : _ref2$optionsOverride;
  var title = post.seo.title;
  var description = post.seo.description || post.synopsis;
  var ogImage = _get(post, 'featuredMedia.mediaDetails.sizes[0]', DEFAULT_OG_IMAGE);
  var ogImageUrl = ogImage.sourceUrl || ogImage.source_url;
  var robots = [];
  if (post.seo.nofollow) {
    robots.push('nofollow');
  }
  if (post.seo.noindex) {
    robots.push('noindex');
  }
  var dynamicMeta = [{
    // Name is required, otherwise this breaks in global-markup
    name: 'article:publisher',
    property: 'article:publisher',
    content: 'http://www.facebook.com/NerdWallet'
  }, {
    name: 'twitter:card',
    content: 'summary_large_image'
  }, {
    name: 'twitter:description',
    content: description
  }, {
    name: 'twitter:site',
    content: '@NerdWallet'
  }, {
    name: 'twitter:image',
    content: (post === null || post === void 0 ? void 0 : (_post$seo = post.seo) === null || _post$seo === void 0 ? void 0 : (_post$seo$twitter = _post$seo.twitter) === null || _post$seo$twitter === void 0 ? void 0 : _post$seo$twitter.image) || ogImageUrl
  }];
  var canonicalURL = post.link;
  var dynamicOpts = {
    canonicalURL: canonicalURL,
    description: description,
    dynamicMeta: dynamicMeta,
    imageSrc: ogImageUrl,
    ogTags: {
      title: title,
      description: description,
      image: ogImageUrl,
      'image:secure_url': ogImageUrl,
      'image:width': ogImage.width,
      'image:height': ogImage.height,
      locale: 'en_US',
      type: 'article',
      site_name: 'NerdWallet',
      url: canonicalURL
    },
    pageTaxonomy: [getPageTaxonomyDataElem(post, 'vertical') || 'undefined', getPageTaxonomyDataElem(post, 'topic') || 'undefined', getPageTaxonomyDataElem(post, 'pageType') || 'Navigate', getPageTaxonomyDataElem(post, 'contentType') || 'article'],
    robots: robots,
    title: title,
    wpData: {
      wpPageType: post.type,
      wpPageID: "post_".concat(post.id)
    }
  };

  // Merge in options overrides to support customization.
  // For `robots`, concatenate the arrays instead of overriding
  var dynamicOptsWithOverrides = _mergeWith(dynamicOpts, optionsOverrides, function (objValue, srcValue, key) {
    if (key === 'robots' && _isArray(objValue)) {
      return objValue.concat(srcValue);
    }
    return undefined; // Use default _.merge behavior
  });
  return dynamicOptsWithOverrides;
};
getNerdlayoutDynamicOptions.fragment = gql(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  fragment getNerdlayoutDynamicOptions on PostInterface {\n    __typename\n    id\n    type\n    link\n    ...getPageTaxonomyData\n    ... on Article {\n      synopsis\n    }\n    ... on PostWithSEOInterface {\n      seo {\n        title\n        description\n        nofollow\n        noindex\n        keyword\n        facebook {\n          title\n          description\n        }\n        twitter {\n          title\n          description\n          image\n        }\n      }\n    }\n    ... on PostWithFeaturedImageInterface {\n      featuredMedia {\n        id\n        mediaDetails {\n          id # Required for ApolloClient 3 to cache properly\n          sizes(sizes: [\"primary-xl\", \"full\"]) {\n            sourceUrl\n            width\n            height\n          }\n        }\n      }\n    }\n  }\n  ", "\n"])), getPageTaxonomyData.fragment);